import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchKnowledgeGraphMultiMode } from '../services/dashboardService';

const ENTITY_COLORS = {
  COMPANY: '#3b82f6',
  PERSON: '#22c55e',
  PROPERTY: '#f59e0b',
  LEGAL_CASE: '#ef4444',
};
const entityColor = (type) => ENTITY_COLORS[type] || '#9ca3af';

const getRatingLabel = (rating) => {
  if (rating >= 0.7) return { label: 'High', cls: 'rating-high' };
  if (rating >= 0.4) return { label: 'Medium', cls: 'rating-med' };
  return { label: 'Low', cls: 'rating-low' };
};

// ── Individual result renderers ────────────────────────────────────────────

const CommunityResult = ({ item, onSelectCommunity }) => {
  const { label, cls } = getRatingLabel(item.rating ?? 0);
  return (
    <div className="sr-result-item sr-community" onClick={() => onSelectCommunity && onSelectCommunity(item)}>
      <div className="sr-result-header">
        <span className={`community-rating-badge ${cls}`}>{label}</span>
        <strong>{item.title}</strong>
        <span className="sr-node-pill">{item.node_count ?? 0} entities</span>
      </div>
      <p className="sr-result-summary">{item.summary}</p>
    </div>
  );
};

const EntityResult = ({ item, onSelectEntity }) => (
  <div className="sr-result-item sr-entity" onClick={() => onSelectEntity && onSelectEntity(item)}>
    <span className="sr-type-chip" style={{ background: entityColor(item.entity_type) }}>
      {item.entity_type || 'ENTITY'}
    </span>
    <div className="sr-result-body">
      <strong>{item.entity_name}</strong>
      {item.description && <p className="sr-result-summary">{item.description}</p>}
    </div>
    <i className="fas fa-chevron-right sr-chevron"></i>
  </div>
);

const EvidenceResult = ({ item, query, onSelectCommunity }) => {
  const [expanded, setExpanded] = useState(false);
  const text = item.text_content || '';
  const preview = text.length > 400 ? text.slice(0, 400) + '…' : text;

  const highlightText = (str) => {
    if (!query) return str;
    const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = str.split(re);
    return parts.map((p, i) =>
      re.test(p) ? <mark key={i}>{p}</mark> : p
    );
  };

  return (
    <div className="sr-result-item sr-evidence">
      <blockquote className="sr-evidence-quote">
        {highlightText(expanded ? text : preview)}
        {text.length > 400 && (
          <button className="sr-expand-btn" onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? 'Show less' : 'Show full excerpt'}
          </button>
        )}
      </blockquote>
      <div className="sr-evidence-meta">
        {item.source?.file_name && (
          <a href={item.source?.url} target="_blank" rel="noopener noreferrer" className="sr-source-link">
            <i className="fas fa-file-pdf"></i> {item.source.file_name}
          </a>
        )}
        {item.source?.provider && (
          <span className="sr-provider-chip">{item.source.provider}</span>
        )}
        {item.chunk_index !== undefined && (
          <span className="sr-chunk-idx">chunk #{item.chunk_index}</span>
        )}
      </div>
      {item.mentioned_entities && item.mentioned_entities.length > 0 && (
        <div className="sr-entity-tags">
          {item.mentioned_entities.map((name, i) => (
            <span key={i} className="sr-entity-tag">{name}</span>
          ))}
        </div>
      )}
    </div>
  );
};

// ── SearchResultsPanel ────────────────────────────────────────────────────

const SearchResultsPanel = ({
  query,
  results,
  loading,
  error,
  activeMode,
  setActiveMode,
  onSelectCommunity,
  onSelectEntity,
}) => {
  if (!query) return null;

  const modes = ['communities', 'nodes', 'chunks'];
  const labels = { communities: 'Topics', nodes: 'Entities', chunks: 'Evidence' };
  const counts = {
    communities: results.communities?.length ?? 0,
    nodes: results.nodes?.length ?? 0,
    chunks: results.chunks?.length ?? 0,
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="sr-loading">
          <div className="sr-spinner"></div> Searching…
        </div>
      );
    }
    if (error) {
      return <div className="sr-error"><i className="fas fa-exclamation-triangle"></i> {error}</div>;
    }

    const list = results[activeMode] || [];
    if (list.length === 0) {
      return (
        <div className="sr-empty">
          No results in this category for "<strong>{query}</strong>".
        </div>
      );
    }

    if (activeMode === 'communities') {
      return list.map((item, i) => (
        <CommunityResult key={item.id ?? i} item={item} onSelectCommunity={onSelectCommunity} />
      ));
    }
    if (activeMode === 'nodes') {
      return list.map((item, i) => (
        <EntityResult key={item.id ?? i} item={item} onSelectEntity={onSelectEntity} />
      ));
    }
    if (activeMode === 'chunks') {
      return list.map((item, i) => (
        <EvidenceResult key={item.chunk_id ?? i} item={item} query={query} onSelectCommunity={onSelectCommunity} />
      ));
    }
    return null;
  };

  return (
    <div className="sr-panel">
      <div className="sr-tabs">
        {modes.map(m => (
          <button
            key={m}
            className={`sr-tab ${activeMode === m ? 'active' : ''}`}
            onClick={() => setActiveMode(m)}
          >
            {labels[m]}
            <span className="sr-tab-count">{counts[m]}</span>
          </button>
        ))}
      </div>
      <div className="sr-results-body">
        {renderContent()}
      </div>
    </div>
  );
};

// ── UnifiedSearchBar (main export) ─────────────────────────────────────────

const UnifiedSearchBar = ({ onSelectCommunity, onSelectEntity, placeholder }) => {
  const [query, setQuery] = useState('');
  const [activeMode, setActiveMode] = useState('communities');
  const [results, setResults] = useState({ communities: [], nodes: [], chunks: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const runSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setResults({ communities: [], nodes: [], chunks: [] });
      setOpen(false);
      return;
    }
    setLoading(true);
    setError(null);
    setOpen(true);
    try {
      // Run all three modes in parallel
      const [commRes, nodesRes, chunksRes] = await Promise.all([
        searchKnowledgeGraphMultiMode(q, 'communities', 10),
        searchKnowledgeGraphMultiMode(q, 'nodes', 10),
        searchKnowledgeGraphMultiMode(q, 'chunks', 10),
      ]);
      setResults({
        communities: commRes.success ? commRes.data || [] : [],
        nodes: nodesRes.success ? nodesRes.data || [] : [],
        chunks: chunksRes.success ? chunksRes.data || [] : [],
      });
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(val), 300);
  };

  const handleClear = () => {
    setQuery('');
    setResults({ communities: [], nodes: [], chunks: [] });
    setOpen(false);
  };

  return (
    <div className="usb-container" ref={containerRef}>
      <div className="usb-bar">
        <i className="fas fa-search usb-icon"></i>
        <input
          type="text"
          className="usb-input"
          placeholder={placeholder || 'Search topics, entities, or evidence…'}
          value={query}
          onChange={handleChange}
          onFocus={() => query && setOpen(true)}
        />
        {query && (
          <button className="usb-clear" onClick={handleClear} title="Clear">
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {open && (
        <SearchResultsPanel
          query={query}
          results={results}
          loading={loading}
          error={error}
          activeMode={activeMode}
          setActiveMode={setActiveMode}
          onSelectCommunity={(item) => {
            setOpen(false);
            if (onSelectCommunity) onSelectCommunity(item);
          }}
          onSelectEntity={(item) => {
            setOpen(false);
            if (onSelectEntity) onSelectEntity(item);
          }}
        />
      )}
    </div>
  );
};

export { SearchResultsPanel };
export default UnifiedSearchBar;
